/**
 * Engagement Domain Model
 *
 * Encapsulates a visitor session from the ClozeSure homepage.
 * Each document in the `engagement` collection represents one anonymous browser
 * session (identified by a random sessionId stored in sessionStorage).
 *
 * Read-only for the dashboard — writes are handled by the recordEngagement
 * Cloud Function on the homepage.
 */

export class Engagement {
  constructor(data = {}) {
    // Core identification
    this.id = data.id || null; // sessionId (Firestore doc key)

    // Environment — filter staging data out of production dashboards
    this.environment = data.environment || 'production';

    // Timestamps
    this.lastSeen = data.lastSeen || null; // Firestore Timestamp

    // Aggregates — pre-computed by the Cloud Function for fast querying
    this.sliderUseCount = data.sliderUseCount || 0;
    this.lastSliderPrice = data.lastSliderPrice || null;
    this.ctaClicked = data.ctaClicked || false;
    this.ctaSource = data.ctaSource || null;

    // First-touch UTM attribution (from go.clozesure.com redirects)
    this.utmSource   = data.utmSource   || null;
    this.utmMedium   = data.utmMedium   || null;
    this.utmCampaign = data.utmCampaign || null;

    // First-touch landing referrer — where the visitor was immediately before
    // the homepage. Host+path only, query stripped (see recordEngagement).
    // landingReferrer e.g. 'www.clozesure.com/news/x'; referrerHost e.g. 'google.com'.
    this.landingReferrer = data.landingReferrer || null;
    this.referrerHost    = data.referrerHost || null;

    // Quote link (set on quote_form_submit)
    // This is the random quote UUID, NOT the numeric HubSpot deal ID.
    // NOTE: recordEngagement (Cloud Function) writes this field as `quoteId`.
    // `data.dealId` is kept as a fallback for any legacy docs.
    this.dealId = data.quoteId || data.dealId || null;

    // Consented HubSpot visitor token (Phase 4) — pseudonymous; present only for
    // cookie-consenting visitors. Sessions sharing it belong to one visitor.
    this.hubspotutk = data.hubspotutk || null;

    // Team's own test traffic — IP-classified at write time, or manually
    // flagged after the fact (see functions/internalTraffic.js). Excluded from
    // Engagement views by default.
    this.isInternal = !!data.isInternal;

    // Full event log — append-only array
    this.events = data.events || [];
  }

  // ========================================
  // COMPUTED PROPERTIES
  // ========================================

  /**
   * First event timestamp as a Date (derived from events[0].ts)
   * @returns {Date|null}
   */
  get firstSeen() {
    if (!this.events || this.events.length === 0) return null;
    return new Date(this.events[0].ts);
  }

  /**
   * Session duration in milliseconds (last event ts - first event ts)
   * @returns {number}
   */
  get sessionDurationMs() {
    if (!this.events || this.events.length < 2) return 0;
    return this.events[this.events.length - 1].ts - this.events[0].ts;
  }

  /**
   * Whether this session submitted the quote form
   * @returns {boolean}
   */
  get isConverted() {
    return !!this.dealId;
  }

  /**
   * Whether this session used the price slider
   * @returns {boolean}
   */
  get usedSlider() {
    return this.sliderUseCount > 0;
  }

  /**
   * Most recent event
   * @returns {Object|null}
   */
  get latestEvent() {
    if (!this.events || this.events.length === 0) return null;
    return this.events[this.events.length - 1];
  }

  /**
   * All CTA click events in this session (in order).
   * Uses the events array so every click is captured, not just the last-wins
   * ctaSource aggregate field.
   * @returns {Array<{type, ts, data}>}
   */
  get ctaEvents() {
    return (this.events || []).filter(
      e => e.type === 'cta_click' || e.type === 'slider_cta_click'
    );
  }

  /**
   * Sections this session scrolled to (deduplicated, in order first seen)
   * @returns {string[]}
   */
  get sectionViews() {
    const seen = new Set();
    const result = [];
    (this.events || [])
      .filter(e => e.type === 'section_view' && e.data?.section)
      .forEach(e => {
        if (!seen.has(e.data.section)) {
          seen.add(e.data.section);
          result.push(e.data.section);
        }
      });
    return result;
  }

  /**
   * Human-readable session duration string
   * @returns {string}
   */
  get durationLabel() {
    const ms = this.sessionDurationMs;
    if (ms < 1000) return '< 1s';
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    const mins = Math.floor(ms / 60000);
    const secs = Math.round((ms % 60000) / 1000);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }

  /**
   * Short display ID (last 8 chars of session ID)
   * @returns {string}
   */
  get shortId() {
    if (!this.id) return 'unknown';
    const parts = this.id.split('_');
    return parts.length > 1 ? parts[parts.length - 1] : this.id.slice(-8);
  }

  // ========================================
  // SERIALIZATION
  // ========================================

  /**
   * Deserialize from Firestore document snapshot
   * @param {DocumentSnapshot} doc
   * @returns {Engagement}
   */
  static fromFirestore(doc) {
    if (!doc) return null;
    const data = doc.data ? doc.data() : doc;
    const id = doc.id || data.id;
    return new Engagement({ id, ...data });
  }

  /**
   * Serialize to plain Firestore document
   * @returns {Object}
   */
  toFirestore() {
    return {
      environment: this.environment,
      lastSeen: this.lastSeen,
      sliderUseCount: this.sliderUseCount,
      lastSliderPrice: this.lastSliderPrice,
      ctaClicked: this.ctaClicked,
      ctaSource: this.ctaSource,
      dealId: this.dealId,
      events: this.events,
    };
  }

  /**
   * Plain object for JSON serialization / RawDataButton
   * @returns {Object}
   */
  toJSON() {
    return {
      id: this.id,
      environment: this.environment,
      firstSeen: this.firstSeen?.toISOString() || null,
      lastSeen: this.lastSeen,
      sessionDurationMs: this.sessionDurationMs,
      durationLabel: this.durationLabel,
      sliderUseCount: this.sliderUseCount,
      lastSliderPrice: this.lastSliderPrice,
      ctaClicked: this.ctaClicked,
      ctaSource: this.ctaSource,
      landingReferrer: this.landingReferrer,
      referrerHost: this.referrerHost,
      isConverted: this.isConverted,
      dealId: this.dealId,
      isInternal: this.isInternal,
      eventCount: this.events.length,
      sectionViews: this.sectionViews,
    };
  }
}
